/**
 * KAN-811: вход менеджера в кабинет (только владелец): создать вход, сменить
 * пароль, отключить и включить. После любого действия справочник и карточка
 * менеджера перечитываются (MANAGERS_QUERY_KEY). Ошибка сервера отдаётся
 * текстом для показа в блоке.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { managersApi } from '@/services/api/managers';
import { MANAGERS_QUERY_KEY } from '@/services/api/queryKeys';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { ManagerAccountCreateBody } from '@/types/manager';

const ERROR_FALLBACK = 'Не удалось сохранить вход';

function errorText(err: unknown): string | null {
  return err ? extractErrorMessage(err, ERROR_FALLBACK) : null;
}

export function useManagerAccount(managerId: string) {
  const client = useQueryClient();
  const onSuccess = () => {
    void client.invalidateQueries({ queryKey: MANAGERS_QUERY_KEY });
  };

  const create = useMutation({
    mutationFn: (body: ManagerAccountCreateBody) => managersApi.createAccount(managerId, body),
    onSuccess,
  });
  const changePassword = useMutation({
    mutationFn: (password: string) => managersApi.updateAccount(managerId, { password }),
    onSuccess,
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => managersApi.setAccountEnabled(managerId, enabled),
    onSuccess,
  });

  return {
    create,
    changePassword,
    setEnabled,
    /** Ошибка создания или включения/отключения (смена пароля показывает свою в диалоге). */
    error: errorText(create.error) ?? errorText(setEnabled.error),
    passwordError: errorText(changePassword.error),
  };
}
